import type { SuperHubGuideReferencePage } from '@core/store';
import type { GitSidebarCategory, GitSidebarPage } from '@readme/api-contracts/schemas/sidebar';
import type { PageVote } from '@ui/PageThumbs';

import { Fragment, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useMetricsAPIFetcher } from '@core/hooks/useMetricsAPI';
import usePagination from '@core/hooks/usePagination';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { normalizeGitPagesToMongo } from '@routes/SuperHub/Editor/Form/components/WhatsNext';
import Banner from '@ui/Banner';
import DateLine, { LocalizedDateLine } from '@ui/DateLine';
import Flex from '@ui/Flex';
import PageThumbs from '@ui/PageThumbs';
import PaginationControls from '@ui/PaginationControls';
import WhatsNext from '@ui/WhatsNext';

/**
 * Normalizes our git sidebar page objects into the expected format that
 * `PaginationControls` expects, which is a generic data object with a bunch of
 * page document fields.
 */
function normalizeToPaginationControls(data?: GitSidebarPage) {
  if (!data) return undefined;
  return {
    link_external: !!data.link_external,
    link_url: data.link_url ?? '',
    pageType: undefined, // TBD: For real-time pages only
    slug: data.slug,
    title: data.title,
    type: data.type,
  };
}

interface SuperhubDocFooterProps {
  document?: SuperHubGuideReferencePage;
  mdxishEnabled?: boolean;
  metricsThumbsEnabled: boolean;
  projectSubdomain: string;
  showRenderEngineBanner?: boolean;
  sidebar: GitSidebarCategory[];
}

/**
 * This is a fork of the @ui/DocFooter/index.jsx component that is used to render the footer of a doc page
 * when the `project.flags.superHub` is enabled.
 *
 * Because SuperHub uses APIv2 data, and the shape of that data is different from internal
 * Hub api data, we need to fork this component to handle the differences.
 */
function SuperhubDocFooter({
  document,
  showRenderEngineBanner = false,
  mdxishEnabled = false,
  metricsThumbsEnabled,
  projectSubdomain,
  sidebar,
}: SuperhubDocFooterProps) {
  const [currentVersion, isReference, isi18nEnabled, language] = useSuperHubStore(s => [
    s.getCurrentVersion(),
    s.routeSection === 'reference',
    s.isI18nEnabled,
    s.language,
  ]);

  const renderingEngine = (() => {
    if (mdxishEnabled) {
      return 'mdxish';
    }
    if (document?.renderable?.status) {
      return 'mdx';
    }
    return 'md';
  })();

  // Map rendering engine to banner color
  const bannerKind = (() => {
    if (renderingEngine === 'mdxish') {
      return 'warn'; // Yellow/orange for experimental mdxish
    }
    if (renderingEngine === 'mdx') {
      return 'primary'; // Blue for standard MDX
    }
    return 'secondary'; // Gray for legacy markdown
  })();

  const whatsNextLabel = useProjectStore(s => s.data.appearance.whats_next_label);
  const { next, prev } = usePagination(document, sidebar);
  const location = useLocation();
  const pathname = location?.pathname;

  // metrics is only available for versions and not for other branch types
  const metricsFetch = useMetricsAPIFetcher({ version: currentVersion.name });

  const pageType = isReference ? 'reference' : 'docs';
  const hasNextData = !!document?.content.next.pages.length || !!document?.content.next.description;

  const sendToMetrics = useCallback(
    (body: PageVote) => {
      const { vote, path } = body || {};
      if (typeof vote !== 'number' || !path) return false;

      return metricsFetch({
        path: 'create/thumb',
        method: 'POST',
        body: { requestType: 'pageQuality', ...body },
      });
    },
    [metricsFetch],
  );

  return (
    <Fragment key={pathname}>
      <Flex align="center" justify="between">
        <div className="UpdatedAt">
          {isi18nEnabled ? (
            <LocalizedDateLine
              icon="watch"
              labelPrefix="updated"
              locale={language}
              tag="p"
              time={document?.updated_at || null}
            />
          ) : (
            <DateLine icon="icon icon-watch" prefix="Updated" tag="p" time={document?.updated_at || null} />
          )}
        </div>
        {showRenderEngineBanner ? (
          <div style={{ paddingTop: '20px' }}>
            <Banner isFixed={false} kind={bannerKind} size="xs">
              <span>
                Renderer: <strong>{renderingEngine}</strong>
              </span>
            </Banner>
          </div>
        ) : null}
      </Flex>
      <hr className="NextStepsDivider" />
      {hasNextData ? (
        <WhatsNext
          description={document.content.next.description ?? undefined}
          label={whatsNextLabel ?? undefined}
          pages={normalizeGitPagesToMongo(document.content.next.pages)}
        />
      ) : (
        <PaginationControls
          next={normalizeToPaginationControls(next)}
          pageType={pageType}
          prev={normalizeToPaginationControls(prev)}
        />
      )}
      {!!metricsThumbsEnabled && (
        <PageThumbs pathname={pathname} projectSubdomain={projectSubdomain} sendToMetrics={sendToMetrics} />
      )}
    </Fragment>
  );
}

export default SuperhubDocFooter;
